package com.evoapps.evoshift.ui.user

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.widgets.LocalContextSwitcher
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.viewmodels.DashboardEvent
import com.evoapps.evoshift.viewmodels.DashboardKind
import com.evoapps.evoshift.viewmodels.DashboardViewModel
import com.evoapps.evoshift.viewmodels.dashboardKindFor
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.material3.Scaffold
import androidx.compose.material3.TextButton
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DeepNavy
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.adaptive.contentWidth
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.dashboard_engagement_ended
import evoshift.composeapp.generated.resources.dashboard_workplace_switch
import evoshift.composeapp.generated.resources.error
import org.jetbrains.compose.resources.stringResource

@Composable
fun DashboardScreen(
    viewModel: DashboardViewModel,
    onCreateNewWorkplaceClicked: () -> Unit,
    onNeedsOnboarding: () -> Unit,
    onNavigateToRoster: (String) -> Unit,
    onNavigateToWorkplaceSettings: (String) -> Unit,
    employeeDashboardContent: @Composable (onSwitchWorkplaceClicked: () -> Unit) -> Unit,
    managerDashboardContent: @Composable (onSwitchWorkplaceClicked: () -> Unit) -> Unit,
    ownerDashboardContent: @Composable (onSwitchWorkplaceClicked: () -> Unit) -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val contextSwitcher = LocalContextSwitcher.current

    val onSwitchWorkplaceClicked = { contextSwitcher.open() }


    if (state.availableWorkplaceIds.isEmpty() && !state.isLoading) {
        // Nothing to show without a workplace: the onboarding opens a business or joins one (SH-38).
        LaunchedEffect(Unit) { onNeedsOnboarding() }
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator(color = DarkGold)
        }
    } else {
        if (state.isLoading && state.userPermission == null) {
            // SH-247: the person's role here is not known yet: the dashboard's shape, not a spinner.
            DashboardPlaceholder()
        } else {
            // The richest dashboard the person's capabilities allow; nothing manager-ish once the
            // engagement ended (the rules then deny everything).
            when {
                state.userPermission == null -> {
                     Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                         Text(
                             stringResource(Res.string.error),
                             color = MaterialTheme.colorScheme.error
                         )
                     }
                }
                else -> when (dashboardKindFor(state.capabilities)) {
                    DashboardKind.OWNER -> ownerDashboardContent(onSwitchWorkplaceClicked)
                    DashboardKind.MANAGER -> managerDashboardContent(onSwitchWorkplaceClicked)
                    DashboardKind.EMPLOYEE -> employeeDashboardContent(onSwitchWorkplaceClicked)
                    DashboardKind.ENDED -> EndedEngagement(onSwitchWorkplaceClicked)
                }
            }
        }
    }
}

/**
 * SH-247: the home screen before the person's role at the workplace is known (which dashboard
 * is not decided yet): the shared top bar and the cards every dashboard starts with, as
 * placeholders; two columns on wide windows.
 */
@Composable
fun DashboardPlaceholder() {
    Scaffold(
        containerColor = Cream,
        topBar = { com.evoapps.evoshift.widgets.EvoTopBar(showWorkplace = true, showMonth = true) }
    ) { padding ->
        val widthClass = com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass.current
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .contentWidth(widthClass.wideContentMaxWidth)
                .padding(horizontal = 16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Spacer(Modifier.height(8.dp))
            val todo: @Composable () -> Unit = { com.evoapps.evoshift.widgets.SkeletonCard(lines = 3) }
            val next: @Composable () -> Unit = { NextShiftPlaceholder() }
            val team: @Composable () -> Unit = { TeamSchedulePlaceholder() }
            val chart: @Composable () -> Unit = { ChartPlaceholder() }
            if (widthClass.isCompact) {
                todo(); next(); team()
            } else {
                com.evoapps.evoshift.ui.adaptive.DashboardColumns(listOf(listOf(todo, next), listOf(team, chart)))
            }
        }
    }
}

/** The engagement here has ended: nothing of the workplace shows, only the way to another one. */

@Composable
private fun EndedEngagement(onSwitchWorkplaceClicked: () -> Unit) {
    Scaffold(containerColor = Cream) { padding ->
        Column(
            modifier = Modifier.fillMaxSize().padding(padding).padding(32.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Text(
                stringResource(Res.string.dashboard_engagement_ended),
                style = MaterialTheme.typography.bodyLarge,
                color = DeepNavy,
                textAlign = TextAlign.Center
            )
            Spacer(Modifier.height(16.dp))
            TextButton(onClick = onSwitchWorkplaceClicked) {
                Text(stringResource(Res.string.dashboard_workplace_switch), color = DeepNavy)
            }
        }
    }
}
